import { Component, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NavigationEnd, RouterModule } from '@angular/router';
import { HandleLoadingState } from '../../decorators/handle-loading-state.decorator';
import { DateEx } from '../../functions/date-ex';
import {
  Alert,
  Location,
  NotificationFrequency,
} from '../../models/api/alerts/alert';
import { MessageCount } from '../../models/api/alerts/message-count';
import { Action } from '../../models/core/action';
import { CommaSpacePipe } from '../../pipes/comma-space.pipe';
import { FormatPipe } from '../../pipes/format.pipe';
import { AlertsDataService } from '../../services/alerts/alerts-data.service';
import { AlertsService } from '../../services/alerts/alerts.service';
import { Snackbar } from '../../services/snackbar/snackbar.service';
import { BaseComponent } from '../base/base.component';
import { ActionButtonComponent } from '../core/buttons/action-button.component';
import { ToggleSwitchComponent } from '../core/controls/toggle-switch.component';
import { DotLabelComponent } from '../core/labels/dot-label.component';
import { TagComponent } from '../core/labels/tag.component';
import { ActionRowComponent } from '../core/panels/action-row.component';
import { DetailItemComponent } from '../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../core/panels/detail-panel/detail-panel.component';
import { MenuComponent } from '../core/panels/menu.component';
import { ColumnComponent } from '../core/table/column.component';
import { TableComponent } from '../core/table/table.component';

@Component({
  selector: 'app-alerts-list',
  imports: [
    TableComponent,
    ColumnComponent,
    TagComponent,
    MenuComponent,
    DetailPanelComponent,
    DetailItemComponent,
    ActionButtonComponent,
    RouterModule,
    ToggleSwitchComponent,
    ActionRowComponent,
    CommaSpacePipe,
    DotLabelComponent,
    FormatPipe,
  ],
  template: `
    <div>
      <app-action-row>
        <app-toggle-switch
          right1
          [control]="showInactive"
          label="Show inactive"
          [showHelpText]="false"
        />
      </app-action-row>

      <div>
        <app-table
          [dataSource]="alertsRecords"
          [expandable]="true"
          [rowExpandLabel]="rowExpandLabel"
          recordLabel="alert"
          dataKey="alertId"
          [ariaTableLabel]="'Manage alert'"
          scrollHeight="800px"
          [loading]="loading"
          [globalFilterFields]="['description', 'status']"
          [showRefresh]="true"
          (refresh)="loadAlerts(true)"
          [loadingHeight]="600"
          [error]="error"
        >
          <app-column
            name="description"
            label="Alert name"
            classes="tw-font-bold"
          />
          @if (showClientColumn) {
            <app-column name="clientName" label="Client" filterType="select" />
          }

          <app-column name="status" label="Status" width="200px">
            <ng-template let-data>
              <app-tag [type]="data.status === 'Active' ? 'Success' : 'Error'">
                {{ data.status }}
              </app-tag>
            </ng-template>
          </app-column>

          <app-column name="messages" label="Messages" width="200px">
            <ng-template let-data>
              <div class="tw-flex tw-items-center tw-gap-2">
                <app-dot-label
                  [label]="
                    data.newMessages +
                    ' unread ' +
                    (data.newMessages === 1 ? 'message' : 'messages')
                  "
                  [color]="
                    data.newMessages > 0
                      ? 'var(--sed-sedgwick-green-color)'
                      : 'var(--sed-grey-100-color)'
                  "
                />
              </div>
            </ng-template>
          </app-column>

          <app-column
            name="actions"
            label="Actions"
            [frozen]="true"
            [sort]="false"
          >
            <ng-template let-data>
              <app-action-button
                [ariaLabel]="rowActionLabel(data)"
                [menu]="rowMenu"
                [row]="data"
                (click)="setCurrentAlert(data); rowMenu.toggle($event, data)"
              />
            </ng-template>
          </app-column>
          <ng-template #expand let-data
            ><div class="tw-flex tw-gap-8 tw-pb-8 tw-pt-4">
              <app-detail-panel [dataSource]="data" class="tw-basis-2/5">
                <app-detail-item label="Client" name="clientName" />
                <app-detail-item
                  label="Line of business"
                  name="lineOfBusiness"
                />
                <app-detail-item
                  label="Location(s)"
                  name="locationsList"
                  labelPosition="Top"
                >
                  <ng-template #value let-data>
                    @if (data.locationsList?.length > 0) {
                      <div class="tw-w-full">
                        <div
                          class="tw-grid tw-grid-cols-12 tw-gap-x-4 tw-text-xs tw-font-bold tw-pb-1 tw-border-b"
                        >
                          <div class="tw-col-span-2">Account</div>
                          <div class="tw-col-span-3">Account Name</div>
                          <div class="tw-col-span-2">Unit</div>
                          <div class="tw-col-span-5">Unit Name</div>
                        </div>
                        <div class="tw-max-h-[250px] tw-overflow-y-auto">
                          @for (loc of data.locationsList; track $index) {
                            <div
                              class="tw-grid tw-grid-cols-12 tw-gap-x-4 tw-py-1 tw-text-xs tw-border-b"
                            >
                              <div class="tw-col-span-2">{{ loc.account }}</div>
                              <div class="tw-col-span-3">
                                {{ loc.accountName }}
                              </div>
                              <div class="tw-col-span-2">{{ loc.unit }}</div>
                              <div class="tw-col-span-5">
                                {{ loc.unitName }}
                              </div>
                            </div>
                          }
                        </div>
                      </div>
                    } @else {
                      <div>{{ data.locationsText }}</div>
                    }
                  </ng-template>
                </app-detail-item>
                <app-detail-item
                  label="State(s) of jurisdiction"
                  name="statesOfJurisdiction"
                />
                <app-detail-item label="State(s) of loss" name="statesOfLoss" />
                <app-detail-item
                  label="State(s) of payroll"
                  name="statesOfPayroll"
                />
                <app-detail-item
                  label="State(s) of premium"
                  name="statesOfPremium"
                />
                <app-detail-item label="Condition" name="condition">
                  <ng-template #value>
                    <div class="tw-whitespace-pre-line">
                      @if (
                        data.conditionAmount !== null &&
                        data.conditionAmount !== undefined
                      ) {
                        {{ data.conditionPrefix }}
                        {{ data.conditionOperatorLabel }}
                        {{ data.conditionAmount | format: 'c' }}.
                      } @else {
                        {{ data.condition | commaSpace }}
                      }
                    </div>
                  </ng-template>
                </app-detail-item>
              </app-detail-panel>

              <app-detail-panel [dataSource]="data" class="tw-basis-1/3">
                <app-detail-item
                  label="Start date"
                  name="startDate"
                  format="d"
                />
                <app-detail-item label="End date" name="endDate" format="d" />
                <app-detail-item
                  label="Alert enabled"
                  name="alertEnable"
                  format="b"
                >
                </app-detail-item>
                <app-detail-item label="Frequency" name="frequency" />
                <app-detail-item label="Send email" name="sendEmail" format="b">
                </app-detail-item>
                <app-detail-item
                  label="Additional email recipients"
                  name="additionalEmailRecipients"
                  classes="tw-flex-nowrap"
                />
                <app-detail-item label="Send text" name="sendText" format="b">
                </app-detail-item>
                <app-detail-item
                  label="Text recipients"
                  name="textRecipients"
                  format="pn"
                  labelPosition="Top"
                />
              </app-detail-panel></div
          ></ng-template>
        </app-table>
        <app-menu [items]="rowActions" #rowMenu (click)="onAction($event)" />
      </div>
    </div>
  `,
  styles: ``,
})
export class AlertsListComponent extends BaseComponent implements OnInit {
  private snackbar = inject(Snackbar);
  private alertsDataService = inject(AlertsDataService);
  private alertsService = inject(AlertsService);

  showInactive = new FormControl<boolean>(false);
  loading = false;
  error: any = null;
  currentAlert: AlertListRow | null = null;

  rowActions: Action[] = [
    { label: 'Edit', key: 'edit' },
    { label: 'Mark all as read', key: 'mark-read' },
    { label: 'Delete', key: 'delete' },
  ];

  alertsRecords: AlertListRow[] = [];
  allAlerts: AlertListRow[] = [];

  /** contractNum:fieldNumber → list value code → description (val_desc) for custom-field Manage Alerts text */
  private customFieldListDescriptions = new Map<string, Map<string, string>>();

  showClientColumn = true;

  protected rowExpandLabel(row: AlertListRow): string {
    return row.description?.trim() || 'alert';
  }

  protected rowActionLabel(row: AlertListRow): string {
    const name = row.description?.trim();
    return name ? `${name} Actions` : 'Actions';
  }

  @HandleLoadingState()
  async ngOnInit(): Promise<void> {
    this.showInactive.setValue(false);
    await this.loadAlerts();

    try {
      const listResponse = await this.api.list.get();
      this.showClientColumn = listResponse.clients.length > 1;
    } catch {
      this.showClientColumn = false;
    }

    this.showInactive.valueChanges.subscribe(() => {
      this.filterAlerts();
    });

    this.dispose.subs = this.router.events.subscribe(async (event) => {
      if (event instanceof NavigationEnd) {
        if (event.url.startsWith('/alerts')) {
          await this.loadAlerts();
        }
      }
    });

    this.dispose.subs = this.alertsDataService.messagesChanged.subscribe(
      (messages) => {
        this.updateMessageCountsFromMessages(messages);
      },
    );
  }

  private updateMessageCountsFromMessages(messages: any[]): void {
    if (!messages || !this.allAlerts.length) {
      return;
    }

    const countMap = new Map<number, { newMessages: number; total: number }>();
    for (const msg of messages) {
      const alertId = msg.alertId;
      if (!alertId) {
        continue;
      }

      if (!countMap.has(alertId)) {
        countMap.set(alertId, { newMessages: 0, total: 0 });
      }

      const counts = countMap.get(alertId)!;
      counts.total++;
      if (msg.messageStatus === 'NEW') {
        counts.newMessages++;
      }
    }

    for (const row of this.allAlerts) {
      const counts = countMap.get(row.alertId);
      if (counts) {
        row.newMessages = counts.newMessages;
        row.totalMessages = counts.total;
        row.messages = `${counts.total}`;
      }
    }

    this.alertsRecords = [...this.alertsRecords];
  }

  /**
   * Load alerts from the API
   */
  @HandleLoadingState()
  protected async loadAlerts(refreshCache = false): Promise<void> {
    // Fetch alerts and message counts in parallel (refreshCache ensures fresh data after save)
    const [alerts, messageCounts] = await Promise.all([
      this.alertsDataService.getAlerts(refreshCache),
      this.alertsDataService.getMessageCounts(refreshCache),
    ]);

    await this.prefetchCustomFieldListDescriptions(alerts);

    // Create a map for quick lookup of message counts
    const countsMap = new Map<number, MessageCount>();
    for (const count of messageCounts) {
      countsMap.set(count.alertId, count);
    }

    // Map alerts to display rows
    this.allAlerts = alerts.map((alert) =>
      this.mapToAlertRow(alert, countsMap),
    );
    this.filterAlerts();
  }

  /**
   * Map Alert from API to AlertListRow for display
   */
  private mapToAlertRow(
    alert: Alert,
    countsMap: Map<number, MessageCount>,
  ): AlertListRow {
    const count = countsMap.get(alert.alertId);
    const newMessages = count?.newMessages ?? 0;
    const totalMessages = count?.totalMessages ?? 0;

    // Classic marks alerts inactive when disabled, not started yet, or end date is before today (date-only, inclusive).
    const enabled = alert.enabled === true;
    const startDate = DateEx.parseDateOnly(alert.startDate);
    const endDate = DateEx.parseDateOnly(alert.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isInactive =
      !enabled ||
      (startDate != null && startDate > today) ||
      (endDate != null && endDate < today);
    const isActive = !isInactive;

    const financialParts = this.getConditionFinancialParts(alert);
    const restrictionLabels = this.getConditionRestrictionLabels(alert);
    return {
      alertId: alert.alertId,
      description: alert.description ?? '',
      condition: this.getConditionDisplayText(alert),
      ...(financialParts
        ? {
            conditionPrefix: financialParts.prefix,
            conditionOperatorLabel: financialParts.operatorLabel,
            conditionAmount: financialParts.amount,
          }
        : {}),
      ...restrictionLabels,
      status: isActive ? 'Active' : 'Inactive',
      messages: `${totalMessages}`,
      newMessages: newMessages,
      totalMessages: totalMessages,
      clientName:
        alert.contracts
          ?.map((c) => c.contNum + ' - ' + c.description)
          .join(', ') ?? '',
      lineOfBusiness: alert.linesOfBusiness?.join(', ') ?? '',
      locationsList: this.buildLocationRows(alert.locations),
      locationsText: alert.locationText ?? '',
      statesOfJurisdiction:
        alert.statesOfJurisdiction && alert.statesOfJurisdiction.length > 0
          ? alert.statesOfJurisdiction
              .map((s) => s.code || s.name)
              .filter(Boolean)
              .join(', ')
          : (alert.statesOfJurisdictionText ?? ''),
      statesOfLoss:
        alert.statesOfLoss && alert.statesOfLoss.length > 0
          ? alert.statesOfLoss
              .map((s) => s.code || s.name)
              .filter(Boolean)
              .join(', ')
          : (alert.statesOfLossText ?? ''),
      statesOfPayroll:
        alert.statesOfPayroll && alert.statesOfPayroll.length > 0
          ? alert.statesOfPayroll
              .map((s) => s.code || s.name)
              .filter(Boolean)
              .join(', ')
          : (alert.statesOfPayrollText ?? ''),
      statesOfPremium:
        alert.statesOfPremium && alert.statesOfPremium.length > 0
          ? alert.statesOfPremium
              .map((s) => s.code || s.name)
              .filter(Boolean)
              .join(', ')
          : (alert.statesOfPremiumText ?? ''),
      startDate: startDate ?? undefined,
      endDate: endDate ?? undefined,
      alertEnable: enabled,
      frequency: this.getFrequencyLabel(alert.notificationFreq),
      sendEmail: alert.sendEmail,
      additionalEmailRecipients:
        alert.additionalEmails?.map((e) => e.emailAddress).join(', ') ?? '',
      sendText: alert.sendText,
      textRecipients:
        alert.smsPhones?.map((p) => p.phoneNumber).join(',') ?? '',
    };
  }

  /**
   * Financial condition types that use a currency amount threshold.
   * Used to display condition amount with FormatPipe in the list.
   */
  private static readonly FINANCIAL_CONDITION_TYPES = new Set<string>([
    'TotalIncurred',
    'ReserveChange',
    'PaymentAmount',
  ]);

  private static readonly FINANCIAL_CONDITION_TYPE_CODES = new Set<number>([
    40019, // PaymentAmount
    40020, // ReserveChange
    40021, // TotalIncurred
  ]);

  /**
   * Maps amount operator (from API/XML) to display label for condition text.
   */
  private getAmountOperatorLabel(
    operatorType: string | null | undefined,
  ): string {
    if (!operatorType) {
      return '';
    }
    const key = String(operatorType).trim();
    const map: Record<string, string> = {
      GreaterThan: 'greater than',
      GreaterThanEqualTo: 'greater than or equal to',
      LessThan: 'less than',
      LessThanEqualTo: 'less than or equal to',
      EqualTo: 'equal to',
      IncreasedBy: 'increased by',
      DecreasedBy: 'decreased by',
      'greater-than': 'greater than',
      'greater-than-equal-to': 'greater than or equal to',
      'less-than': 'less than',
      'less-than-equal-to': 'less than or equal to',
      'equal-to': 'equal to',
      inc: 'increased by',
      dec: 'decreased by',
    };
    return map[key] ?? key;
  }

  /**
   * For TotalIncurred, ReserveChange, PaymentAmount: extracts prefix, operator label, and numeric amount
   * so the template can render the amount with FormatPipe. Returns null for other types or invalid data.
   */
  private getConditionFinancialParts(
    alert: Alert,
  ): { prefix: string; operatorLabel: string; amount: number } | null {
    const cond = alert.condition;
    if (!cond?.newValues?.length) {
      return null;
    }
    const type = cond.conditionType != null ? String(cond.conditionType) : '';
    const typeNum = Number(cond.conditionType);
    const isFinancial =
      AlertsListComponent.FINANCIAL_CONDITION_TYPES.has(type) ||
      AlertsListComponent.FINANCIAL_CONDITION_TYPE_CODES.has(typeNum);
    if (!isFinancial) {
      return null;
    }
    const amountRaw = cond.newValues[0]?.value;
    if (amountRaw == null || String(amountRaw).trim() === '') {
      return null;
    }
    const amount = parseFloat(String(amountRaw).replace(/[$,]/g, ''));
    if (Number.isNaN(amount)) {
      return null;
    }
    const operatorLabel = this.getAmountOperatorLabel(cond.operatorType);
    const prefix =
      type === 'TotalIncurred' || typeNum === 40021
        ? 'The total incurred amount'
        : type === 'ReserveChange' || typeNum === 40020
          ? 'The total future reserve amount'
          : 'Payment amount is';
    return { prefix, operatorLabel, amount };
  }

  /**
   * For CalendarRestrictedDays only: returns labels and values for "Previous restriction days" /
   * "Current restriction days" shown under the condition. Returns empty for all other condition types
   * (including ClientDefinedField / Custom Field) so those lines are not displayed.
   */
  private getConditionRestrictionLabels(
    alert: Alert,
  ): Pick<
    AlertListRow,
    | 'showRestrictionDays'
    | 'conditionPreviousLabel'
    | 'conditionCurrentLabel'
    | 'conditionPreviousValue'
    | 'conditionCurrentValue'
  > {
    const cond = alert.condition;
    const type = cond?.conditionType != null ? String(cond.conditionType) : '';
    const typeNum = Number(cond?.conditionType);
    const typeLower = type.toLowerCase();

    // Custom Field (any data type, including Date) — never show restriction labels.
    // Date custom fields use days/notify in value1 but are still ClientDefinedField, not CalendarRestrictedDays.
    const isClientDefinedField =
      typeLower === 'clientdefinedfield' || type === '31' || typeNum === 31;
    if (isClientDefinedField) {
      return {};
    }

    // If conditionType is wrong/missing but value1 matches custom-field pipe format (fieldNum|operator|...), hide labels.
    const firstValue = cond?.newValues?.[0]?.value;
    if (this.looksLikeCustomFieldValue1(firstValue)) {
      return {};
    }

    const isCalendarRestrictedDays =
      type === 'CalendarRestrictedDays' || typeNum === 26;

    if (!isCalendarRestrictedDays || !cond?.newValues?.length) {
      return {};
    }

    const daysBefore = cond.newValues.find(
      (nv) => nv?.name?.trim() === 'daysBefore',
    )?.value;

    return {
      showRestrictionDays: true,
      conditionPreviousLabel: 'Previous restriction days',
      conditionCurrentLabel: 'Current restriction days',
      conditionPreviousValue: '',
      conditionCurrentValue: daysBefore ?? '',
    };
  }

  /**
   * Custom Field alert value1 is stored as pipe-separated segments starting with field number (e.g. "54|equalTo|...").
   * Used to avoid showing CalendarRestrictedDays UI when conditionType is mis-serialized.
   */
  private looksLikeCustomFieldValue1(
    value: string | null | undefined,
  ): boolean {
    if (value == null || typeof value !== 'string') {
      return false;
    }
    const trimmed = value.trim();
    return /^\d+\|/.test(trimmed);
  }

  /**
   * Loads misc field lookup values so in-list custom fields show exact val_desc text in the condition line.
   */
  @HandleLoadingState({ errorProperty: 'actionError' })
  private async prefetchCustomFieldListDescriptions(
    alerts: Alert[],
  ): Promise<void> {
    this.customFieldListDescriptions.clear();
    const needed = new Map<string, { contract: number; fieldNumber: number }>();
    for (const alert of alerts) {
      const cond = alert.condition;
      const type =
        cond?.conditionType != null ? String(cond.conditionType) : '';
      const typeNum = Number(cond?.conditionType);
      const isClientDefinedField =
        type === 'ClientDefinedField' || typeNum === 31;
      if (!isClientDefinedField) {
        continue;
      }
      const raw = cond?.newValues?.[0]?.value;
      if (raw == null || typeof raw !== 'string') {
        continue;
      }
      if (!this.looksLikeCustomFieldValue1(raw)) {
        continue;
      }
      const parts = raw.split('|');
      const fieldNumber = parseInt(parts[0], 10);
      if (Number.isNaN(fieldNumber)) {
        continue;
      }
      const listSeg = (parts[8] ?? '').trim();
      const opLower = (parts[1] ?? '').toLowerCase();
      if (listSeg === '' && opLower !== 'inlist' && opLower !== 'notinlist') {
        continue;
      }
      const contract = alert.contracts?.[0]?.contNum;
      if (contract == null) {
        continue;
      }
      const key = `${contract}:${fieldNumber}`;
      if (!needed.has(key)) {
        needed.set(key, { contract, fieldNumber });
      }
    }

    await Promise.all(
      [...needed.values()].map(async ({ contract, fieldNumber }) => {
        const key = `${contract}:${fieldNumber}`;
        try {
          const values = await this.alertsService.loadMiscFieldValues(
            contract,
            fieldNumber,
          );
          const m = new Map<string, string>();
          for (const x of values) {
            if (x.value != null && String(x.value).trim() !== '') {
              m.set(
                String(x.value),
                x.description != null && String(x.description).trim() !== ''
                  ? String(x.description)
                  : String(x.value),
              );
            }
          }
          this.customFieldListDescriptions.set(key, m);
        } catch {
          // Display falls back to codes in value1
        }
      }),
    );
  }

  /**
   * Returns the condition text to display in the Condition section.
   * For ClientDefinedField, builds the formatted expression from newValues[0].value (pipe format)
   * whenever present so wording stays correct (e.g. "between 1 and 40") even if cond.text
   * was saved with an older template ("between 1 40").
   */
  private getConditionDisplayText(alert: Alert): string {
    const cond = alert.condition;
    const stored = cond?.text ?? '';
    const type = cond?.conditionType != null ? String(cond.conditionType) : '';
    if (type === 'MultipleOpenClaims' || Number(cond?.conditionType) === 10) {
      return 'Multiple open claims for claimant';
    }
    const isClientDefinedField =
      type === 'ClientDefinedField' || Number(cond?.conditionType) === 31;
    const cardDescription =
      'Monitor when a client-defined custom field value changes or matches selected criteria';
    const nv0 = cond?.newValues?.[0];
    const v = nv0?.value;
    const isPipeValue =
      typeof v === 'string' && this.looksLikeCustomFieldValue1(v);
    const useStoredFallback =
      !stored.trim() || stored.trim() === cardDescription;

    if (
      isClientDefinedField &&
      nv0 != null &&
      typeof v === 'string' &&
      (isPipeValue || useStoredFallback)
    ) {
      const rawName = nv0.name?.trim() || '';
      const fieldName =
        rawName && !/^Value\s*1$/i.test(rawName) ? rawName : 'Custom field';
      const contract = alert.contracts?.[0]?.contNum;
      let listMap: Map<string, string> | undefined;
      if (contract != null && v.trim() !== '') {
        const parts = v.split('|');
        const fn = parseInt(parts[0], 10);
        if (!Number.isNaN(fn)) {
          listMap = this.customFieldListDescriptions.get(`${contract}:${fn}`);
        }
      }
      return this.alertsService.getCustomFieldConditionDisplayText(
        fieldName,
        v,
        listMap,
      );
    }
    return stored;
  }

  private buildLocationRows(locations: Location[]): AlertLocationRow[] {
    if (!locations?.length) {
      return [];
    }

    return locations.map((loc) => ({
      account: loc.accountNumber ? loc.accountNumber.toString() : '',
      accountName: loc.locationName ?? '',
      unit: loc.unitNumber ? loc.unitNumber.toString().padStart(4, '0') : '',
      unitName: loc.locationName ?? '',
    }));
  }

  filterAlerts() {
    const showInactive = this.showInactive.value;
    const filtered = showInactive
      ? this.allAlerts
      : this.allAlerts.filter((alert) => alert.status === 'Active');
    this.alertsRecords = this.sortAlerts(filtered);
  }

  private sortAlerts(alerts: AlertListRow[]): AlertListRow[] {
    return [...alerts].sort((a, b) =>
      a.description.localeCompare(b.description, undefined, {
        sensitivity: 'base',
      }),
    );
  }

  setCurrentAlert(data: AlertListRow) {
    this.currentAlert = data;
  }

  onAction(action: Action) {
    switch (action.key) {
      case 'edit':
        if (this.currentAlert) {
          this.router.navigate(['alerts/edit-alert'], {
            queryParams: { alertId: this.currentAlert.alertId },
          });
        }
        break;
      case 'mark-read':
        this.markAlertMessagesAsRead();
        break;
      case 'delete':
        this.deleteAlert();
        break;
    }
  }

  @HandleLoadingState({ errorProperty: 'actionError' })
  private async markAlertMessagesAsRead(): Promise<void> {
    if (!this.currentAlert) return;

    await this.alertsDataService.markAlertMessagesAsRead(
      this.currentAlert.alertId,
    );
    await this.loadAlerts(true);
    this.snackbar.show('All messages marked as read', 'Success');
  }

  private async deleteAlert(): Promise<void> {
    if (!this.currentAlert) return;

    this.confirm.show({
      label: 'Delete alert',
      message: 'The Alert will be permanently deleted.',
      width: '35rem',
      yesLabel: 'Confirm',
      noLabel: 'Close',
      confirm: async () => {
        try {
          const deletedId = this.currentAlert!.alertId;
          await this.alertsDataService.deleteAlert(deletedId);

          // Remove from local arrays without reloading from API
          this.removeFromLocal(deletedId);
          this.currentAlert = null;
          this.snackbar.show('Alert deleted', 'Success');
        } catch (error) {
          this.snackbar.show('Failed to delete alert.', 'Error');
        }
      },
    });
  }

  /**
   * Remove alert from local state without reloading from API
   * Used for individual alert deletions
   */
  private removeFromLocal(alertId: number): void {
    this.allAlerts = this.allAlerts.filter(
      (alert) => alert.alertId !== alertId,
    );
    this.alertsRecords = this.alertsRecords.filter(
      (alert) => alert.alertId !== alertId,
    );
  }

  private getFrequencyLabel(freq: NotificationFrequency): string {
    switch (freq) {
      case 'Daily':
        return 'Daily';
      case 'NearRealTime':
        return 'Near real time';
      default:
        return '';
    }
  }
}

interface AlertListRow {
  alertId: number;
  description: string;
  status: string;
  messages: string;
  newMessages: number;
  totalMessages: number;
  clientName: string;
  lineOfBusiness: string;
  locationsList: AlertLocationRow[];
  locationsText: string;
  statesOfJurisdiction: string;
  statesOfLoss: string;
  statesOfPayroll: string;
  statesOfPremium: string;
  condition: string;
  /** For financial conditions (TotalIncurred, ReserveChange, PaymentAmount): prefix sentence */
  conditionPrefix?: string;
  /** For financial conditions: operator display label */
  conditionOperatorLabel?: string;
  /** For financial conditions: numeric amount (rendered with FormatPipe 'c') */
  conditionAmount?: number | null;
  /** True only for CalendarRestrictedDays; gates "Previous/Current restriction days" block */
  showRestrictionDays?: boolean;
  /** For CalendarRestrictedDays: label for previous value row */
  conditionPreviousLabel?: string;
  /** For CalendarRestrictedDays: label for current value row */
  conditionCurrentLabel?: string;
  /** For CalendarRestrictedDays: previous value (blank per requirement) */
  conditionPreviousValue?: string;
  /** For CalendarRestrictedDays: current numeric value */
  conditionCurrentValue?: string;
  startDate?: Date;
  endDate?: Date;
  alertEnable: boolean;
  frequency: string;
  sendEmail: boolean;
  additionalEmailRecipients: string;
  sendText: boolean;
  textRecipients: string;
}

interface AlertLocationRow {
  account: string;
  accountName: string;
  unit: string;
  unitName: string;
}
